import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherDashboard = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState({
    myQuestions: 0,
    approvedQuestions: 0,
    pendingQuestions: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && (user.role === 'teacher' || user.role === 'admin')) {
      fetchTeacherStats();
    } else if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchTeacherStats = async () => {
    try {
      const myQuestionsRes = await axios.get(`${API}/teacher/my-questions`, { withCredentials: true });
      const questions = myQuestionsRes.data.questions || [];
      
      setStats({
        myQuestions: questions.length,
        approvedQuestions: questions.filter(q => q.status === 'active' || q.status === 'approved').length,
        pendingQuestions: questions.filter(q => q.status === 'pending_approval').length
      });
    } catch (error) {
      console.error('Error fetching teacher stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const teacherSections = [
    {
      id: 'ai-generator',
      icon: '🤖',
      title: language === 'tr' ? 'AI Soru Üretici' : 'AI Question Generator',
      description: language === 'tr' ? 'Yapay zeka ile TYT-AP soru çiftleri üret' : 'Generate TYT-AP question pairs with AI',
      path: '/teacher-portal',
      gradient: 'from-purple-500 to-pink-500',
      highlight: true
    },
    {
      id: 'my-questions',
      icon: '📝',
      title: language === 'tr' ? 'Sorularım' : 'My Questions',
      description: language === 'tr' ? 'Gönderdiğim soruları görüntüle' : 'View my submitted questions',
      count: stats.myQuestions,
      path: '/teacher-portal',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'question-bank',
      icon: '📚',
      title: language === 'tr' ? 'Soru Bankası' : 'Question Bank',
      description: language === 'tr' ? 'Tüm soruları incele ve filtrele' : 'Browse and filter all questions',
      path: '/question-bank',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      id: 'my-booklets',
      icon: '📚',
      title: language === 'tr' ? 'Kitapçıklarım' : 'My Booklets',
      description: language === 'tr' ? 'Oluşturduğunuz kitapçıkları yönetin' : 'Manage your booklets',
      path: '/teacher/my-booklets',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      id: 'create-booklet',
      icon: '➕',
      title: language === 'tr' ? 'Yeni Kitapçık' : 'New Booklet',
      description: language === 'tr' ? 'Soru bankasından seçerek oluştur' : 'Create from question bank',
      path: '/create-booklet-from-bank',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      id: 'students',
      icon: '👥',
      title: language === 'tr' ? 'Öğrenci Takibi' : 'Student Tracking',
      description: language === 'tr' ? 'Öğrenci ilerlemelerini gör' : 'View student progress',
      path: '/teacher/students',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Öğretmen Paneli' : 'Teacher Panel'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                👨‍🏫 {language === 'tr' ? 'Hoş Geldiniz, Öğretmen' : 'Welcome, Teacher'}
              </h1>
              <p className="text-purple-100 text-lg">
                {language === 'tr' 
                  ? 'İçerik oluşturma ve öğrenci yönetimi'
                  : 'Content creation and student management'}
              </p>
            </div>
            <div className="text-6xl">📚</div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">📝</span>
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Toplam' : 'Total'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{stats.myQuestions}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Gönderilen Soru' : 'Submitted Questions'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">✅</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Onaylı' : 'Approved'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{stats.approvedQuestions}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Onaylanan Soru' : 'Approved Questions'}</div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-3xl">⏳</span>
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                {language === 'tr' ? 'Bekliyor' : 'Pending'}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800 mb-1">{stats.pendingQuestions}</div>
            <div className="text-sm text-gray-600">{language === 'tr' ? 'Onay Bekleyen' : 'Pending Approval'}</div>
          </div>
        </div>

        {/* Teacher Sections */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {language === 'tr' ? '🚀 Hızlı Erişim' : '🚀 Quick Access'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {teacherSections.map((section) => (
              <div
                key={section.id}
                onClick={() => navigate(section.path)}
                className={`group bg-white rounded-2xl shadow-lg p-6 cursor-pointer hover:shadow-2xl transform hover:scale-105 transition-all duration-300 ${
                  section.highlight ? 'ring-2 ring-purple-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`inline-flex p-4 bg-gradient-to-br ${section.gradient} rounded-2xl text-4xl shadow-lg`}>
                    {section.icon}
                  </div>
                  {section.count !== undefined && (
                    <div className="bg-purple-500 text-white rounded-full px-3 py-1 font-bold text-sm">
                      {section.count}
                    </div>
                  )}
                  {section.highlight && (
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full px-3 py-1 font-bold text-xs">
                      {language === 'tr' ? 'YENİ' : 'NEW'}
                    </div>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
                  {section.title}
                </h3>
                
                <p className="text-sm text-gray-600 mb-4">
                  {section.description}
                </p>
                
                <div className="flex items-center text-purple-600 font-semibold">
                  <span>{language === 'tr' ? 'Aç' : 'Open'}</span>
                  <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Tips */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            💡 {language === 'tr' ? 'İpuçları' : 'Quick Tips'}
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              {language === 'tr' 
                ? 'AI ile soru üretirken konuyu ve alt becerileri açık bir şekilde belirtin'
                : 'When generating with AI, clearly specify the topic and sub-skills'}
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              {language === 'tr'
                ? 'Manuel eklediğiniz sorular admin onayından sonra aktif olur'
                : 'Manually added questions become active after admin approval'}
            </li>
            <li className="flex items-start">
              <span className="text-green-500 mr-2">✓</span>
              {language === 'tr'
                ? 'Soru bankasından mevcut soruları inceleyerek fikir edinebilirsiniz'
                : 'Review existing questions in the question bank for inspiration'}
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;
